import { FormControl } from '@mui/material';
import { DateTimePicker, DesktopDatePicker } from '@mui/x-date-pickers';
import { esES } from '@mui/x-date-pickers/locales';
import { DateTimeValidationError, TimeView } from '@mui/x-date-pickers/models';
import dayjs, { Dayjs } from 'dayjs';
import { forwardRef, useEffect, useState } from 'react';

export interface DateInputProps {
  name: string;
  placeholder: string;
  value: Date | null;
  onChange: (value: Date | null) => void;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  clearable?: boolean;
  disablePast?: boolean;
  withTime?: boolean;
  error?: boolean;
  errorInfo?: string;
  onBlur?: () => void;
  onError?: (error: DateTimeValidationError) => void;
  shouldDisableDate?: (date: Date) => boolean;
  shouldDisableTime?: (date: Date, view: TimeView) => boolean;
  reportInvalidValue?: boolean;
}

const DateInput = forwardRef<HTMLInputElement, DateInputProps>(
  (
    {
      name,
      placeholder,
      value,
      onChange,
      disabled = false,
      autoFocus = false,
      required = false,
      clearable = false,
      disablePast = false,
      withTime = false,
      error = false,
      errorInfo,
      onBlur,
      onError,
      shouldDisableDate,
      shouldDisableTime,
      reportInvalidValue = false,
    },
    ref
  ) => {
    const [internalValue, setInternalValue] = useState<Dayjs | null>(
      value ? dayjs(value) : null
    );
    useEffect(() => {
      const incoming = value ? dayjs(value) : null;
      setInternalValue((prev) => {
        if (!reportInvalidValue && prev && !prev.isValid()) return prev;
        if (incoming && prev && !incoming.isValid() && !prev.isValid())
          return prev;
        if (!incoming && !prev) return prev;
        if (incoming && prev && prev.isValid() && incoming.isSame(prev))
          return prev;
        return incoming;
      });
    }, [value, reportInvalidValue]);

    const pickerProps = {
      localeText:
        esES.components.MuiLocalizationProvider.defaultProps.localeText,
      name,
      label: placeholder,
      value: internalValue,
      inputRef: ref,
      disabled,
      disablePast,
      autoFocus,
      onChange: (newValue: Dayjs | null) => {
        setInternalValue(newValue);
        // Existing DateInput consumers expect valid dates. Range forms opt in
        // so malformed text cannot leave an old valid value ready to submit.
        if (newValue && !newValue.isValid() && !reportInvalidValue) return;
        onChange(newValue ? newValue.toDate() : null);
      },
      onError,
      shouldDisableDate: shouldDisableDate
        ? (date: Dayjs) => shouldDisableDate(date.toDate())
        : undefined,
      slotProps: {
        field: { clearable },
        textField: {
          fullWidth: true,
          required,
          onBlur,
          ...(error || errorInfo ? { error: true, helperText: errorInfo } : {}),
        },
      },
    };
    return (
      <FormControl
        fullWidth
        disabled={disabled}
        required={required}
        error={error}
      >
        {withTime ? (
          <DateTimePicker
            {...pickerProps}
            desktopModeMediaQuery='(min-width: 768px)'
            ampm={false}
            format='DD/MM/YYYY HH:mm:ss'
            views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
            timeSteps={{ hours: 1, minutes: 1, seconds: 1 }}
            shouldDisableTime={
              shouldDisableTime
                ? (date, view) => shouldDisableTime(date.toDate(), view)
                : undefined
            }
          />
        ) : (
          <DesktopDatePicker {...pickerProps} />
        )}
      </FormControl>
    );
  }
);

DateInput.displayName = 'DateInput';

export default DateInput;
